import { useState } from "react";
import type { Contact, ContactInput, ContactRole, ContactSource } from "@career-ops-amplify/shared";
import { useUpdateContact } from "@/hooks/useContacts";

const ROLES: ContactRole[] = [
  "recruiter",
  "hiring_manager",
  "interviewer",
  "referrer",
  "other",
];

const SOURCES: ContactSource[] = [
  "manual",
  "contacto",
  "interview",
  "referral",
  "import",
];

interface ContactEditModalProps {
  contact: Contact;
  onClose: () => void;
  onSaved?: () => void;
}

export function ContactEditModal({ contact, onClose, onSaved }: ContactEditModalProps) {
  const updateContact = useUpdateContact();
  const [name, setName] = useState(contact.name);
  const [email, setEmail] = useState(contact.email ?? "");
  const [linkedinUrl, setLinkedinUrl] = useState(contact.linkedinUrl ?? "");
  const [role, setRole] = useState<ContactRole>(contact.role ?? "recruiter");
  const [title, setTitle] = useState(contact.title ?? "");
  const [agencyName, setAgencyName] = useState(contact.agencyName ?? "");
  const [source, setSource] = useState<ContactSource>(contact.source ?? "manual");
  const [notes, setNotes] = useState(contact.notes ?? "");
  const [companySlug, setCompanySlug] = useState(contact.companySlug);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const updates: Partial<ContactInput> = {
      name: name.trim(),
      email: email.trim() || undefined,
      linkedinUrl: linkedinUrl.trim() || undefined,
      role,
      title: title.trim() || undefined,
      agencyName: agencyName.trim() || undefined,
      source,
      notes: notes.trim() || undefined,
      companySlug: companySlug.trim(),
    };
    await updateContact.mutateAsync({ id: contact.id, updates });
    onSaved?.();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-lg"
      >
        <h3 className="mb-4 text-lg font-semibold text-foreground">Edit contact</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="text"
            placeholder="Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm sm:col-span-2"
            required
          />
          <input
            type="text"
            placeholder="Company slug *"
            value={companySlug}
            onChange={(e) => setCompanySlug(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            required
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as ContactRole)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replace("_", " ")}
              </option>
            ))}
          </select>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as ContactSource)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <input
            type="url"
            placeholder="LinkedIn URL"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Agency name"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm sm:col-span-2"
          />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted/20"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateContact.isPending || !name.trim()}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {updateContact.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
